/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { CategoriesAPI } from "../../../api/Categories";
import { ContactApi } from "../../../api/Contacts";
import { ActiveCampaignAtom } from "../../../atoms/CampaignAtom";
import { CategoriesAtom } from "../../../atoms/CategoriesAtom";
import { AllContactsTotalAtom } from "../../../atoms/ContactFilterAtom";
import { ContactsAtom } from "../../../atoms/ContactsAtom";
import { SelectedTagsAtom } from "../../../atoms/SelectedTags";
import { TagsAtom } from "../../../atoms/TagsAtom";
import { UpdateContactsFlagAtom } from "../../../atoms/UpdateContactsAtom";
import { IContactTag } from "../../../generated/Api";
import useRowsPerPage, { ITableName } from "../../../hook/useRowsPerPage";
import { notSelect } from "../../../styles/general";
import { IContact } from "../../../types/Contact";
import { ITag } from "../../../types/TagTypes";
import IconBerserkerMail from "../../Icon";
import ApplyTagModal from "../../Modal/container/Tags/ApplyTagModal";
import Warning from "../../Modal/container/Warning";
import { useModal } from "../../Modal/Modal";
import StyledButton from "../../StyledButton";
import {
  getComparator,
  Order,
  stableSort,
  TableFooter,
} from "../../StyledTable";
import TagSelect from "../../TagSelect/TagSelect";
import { DateFilter, EmptyContainer, SkeletonCard } from "./Emails";
import FilterTags, { FilterTag } from "./FilterTags";
import TagsTableRow from "./TagsTableRow";
const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow-y: scroll;
  margin-bottom: 40px;
`;

const TableCard = styled.div`
  display: grid;
  grid-template-columns: 1fr 180px 1fr 20px;
  grid-column-gap: 15px;
  height: 60px;
  background-color: #f8f8f8;
  align-items: center;
  padding: 15px;
`;

const TableHead = styled.div`
  color: #707070;
  display: flex;
  font-size: 14px;
  align-items: center;
  font-weight: 600;
  cursor: default;
  position: relative;
  ${notSelect}
`;

const Icon = styled.img`
  width: 10px;
  height: 10px;
  margin-left: 8px;
  ${notSelect}
  cursor: pointer;
`;

const FlexRow = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  :hover {
    opacity: 0.6;
  }
`;

const TagsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding-bottom: 20px;
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: flex-start;

  div:first-of-type {
    max-width: 348px;
  }

  input {
    width: 348px;
  }

  .e1sv7fj16 {
    width: 348px;
    height: 40px;
    border: 1px solid #dddddd;
    border-radius: 5px;
    outline: none;
    font-size: 15px;
    color: #383838;
    position: relative;
    -webkit-transition: box-shadow 0.3s, border 0.3s;
    transition: box-shadow 0.3s, border 0.3s;
    background-color: #ffffff;

    text-indent: 0 !important;

    * {
      text-indent: 0 !important;
    }

    .e1sv7fj14 {
      position: absolute !important;
      left: 8px !important;
      top: 11px !important;
    }

    .e1sv7fj15 {
      height: 100%;

      input {
        width: calc(100% - 40px);
        height: 100%;
        padding: 10px;
        border: none;
        border-radius: 5px;
        outline: none;
        margin: 0px 20px;
        box-shadow: none;
        font-size: 15px;
      }
    }
  }

  .e1eaopo810 {
    width: 390px;
    height: 40px;
    border: 1px solid #dddddd;
    border-radius: 5px;
    outline: none;
    font-size: 15px;
    color: #383838;
    position: relative;
    -webkit-transition: box-shadow 0.3s, border 0.3s;
    transition: box-shadow 0.3s, border 0.3s;
    background-color: #ffffff;

    text-indent: 0 !important;

    * {
      text-indent: 0 !important;
    }

    .e1eaopo84 {
      top: 4px;
    }

    .e1eaopo80 {
      right: 72px !important;
      top: 9px !important;
    }

    .e1eaopo88 {
      position: absolute !important;
      left: 8px !important;
      top: 11px !important;
    }

    .e1eaopo89 {
      height: 100%;

      .e1eaopo87 {
        width: calc(100% - 40px);
        height: 100%;
        padding: 10px 72px 10px 10px;
        border: none;
        border-radius: 5px;
        outline: none;
        margin: 0px 20px;
        box-shadow: none;
        font-size: 15px;
      }
    }
  }

  .filter-tag {
    margin-left: 12px;
  }

  .ekp3u414 {
    width: 80px;
    height: 40px;
    margin-left: 10px;
  }

  .envmntf9 {
    margin: 16px 0 0 0;
    grid-gap: 16px;

    > div {
      margin: 0;
    }
  }
`;

const SubTitle = styled.div`
  color: #333333;
  font-size: 18px;
  font-weight: 600;
`;

const button = css`
  margin-left: 12px;
  height: 40px;
  width: 120px;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export interface ITagRow {
  _id: string;
  name: string;
  type: string;
  category: string;
  appliedAt: Date;
  isStartTag?: boolean;
  isStopTag?: boolean;
}

interface Props {
  individualTags: IContactTag[];
  contact: IContact;
  searchContacts?: (signal?: AbortSignal) => Promise<{
    contacts: IContact[];
    total: number;
  }>;
}

export default function Tags(props: Props): JSX.Element {
  let tagsPool = useRecoilValue(TagsAtom);
  const { individualTags, contact, searchContacts } = props;
  const setActiveCampaign = useSetRecoilState(ActiveCampaignAtom);
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof ITagRow>("name");
  const [selectedTags, setSelectedTags] = useRecoilState(SelectedTagsAtom);
  const setCategories = useSetRecoilState(CategoriesAtom);
  const [tagsSelected, setTagsSelected] = useState<ITag[]>([]);
  const [tagsInRow, setTagsInRow] = useState<ITagRow[]>([]);
  let setContacts = useSetRecoilState(ContactsAtom);
  const [filteredTags, setFilteredTags] = useState<ITagRow[]>([]);
  const modal = useModal();
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState<FilterTag>({
    categories: [],
    type: [],
    date: DateFilter.Recent,
  });
  const [loading, setLoading] = useState(true);
  const [isApplyingTag, setIsApplyingTag] = useState(false);
  const [isEmpty, setIsEmpty] = useState(false);
  const setUpdateContactsFlag = useSetRecoilState(UpdateContactsFlagAtom);
  const [cachedRowsPerPage, updateCachedRowsPerPage] = useRowsPerPage(
    ITableName.CONTACTS_DETAILS_TAGS
  );
  const [contactITags, setContactITags] = useState<ITag[]>([]);

  const setTotal = useSetRecoilState(AllContactsTotalAtom);

  useEffect(() => {
    (async () => {
      const categoriesResponse = await CategoriesAPI.readAll();

      if (categoriesResponse) {
        setCategories(categoriesResponse);

        if (categoriesResponse.length < 1) {
          setIsEmpty(true);
        }
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!individualTags.length) {
      setLoading(false);
      return;
    }
    const arr: ITagRow[] = [];
    const contactTags: ITag[] = [];
    individualTags.forEach((itag) => {
      const tags = tagsPool.find((tag) => itag._id === tag._id);

      if (!tags) return;

      contactTags.push(tags);
      arr.push({
        _id: tags?._id!,
        name: tags?.name!,
        type: tags?.type!,
        category: tags?.category!,
        appliedAt: new Date(itag?.appliedAt!),
        isStartTag: tags?.isStartTag,
        isStopTag: tags?.isStopTag,
      });
    });
    setTagsInRow(arr);
    setContactITags(contactTags);
    setTimeout(() => setLoading(false), 500);
  }, [individualTags, tagsPool]);

  useEffect(() => {
    setActiveCampaign(null);
  }, []);

  useEffect(() => {
    setPage(0);
  }, []);

  useEffect(() => {
    setFilteredTags(getFilteredTags());
  }, [filter, tagsInRow]);

  function getFilteredTags() {
    if (!tagsInRow) return [];

    let tagsWithFilter: ITagRow[] = [...tagsInRow];

    if (filter.type.length > 0) {
      tagsWithFilter = tagsWithFilter.filter((not) => {
        if (filter.type.find((t) => t === not.type)) return true;
        return false;
      });
    }

    if (filter.categories.length > 0) {
      tagsWithFilter = tagsWithFilter.filter((not) => {
        if (filter.categories.find((c) => c === not.category)) return true;
        return false;
      });
    }

    return tagsWithFilter;
  }

  function handlePage(vle: number) {
    setPage(vle);
  }

  async function handleApplyTag() {
    try {
      setIsApplyingTag(true);
      let newTags = tagsSelected
        .filter((ts) => !props.contact.tags.find((ct) => ct._id === ts._id))
        .map((t, i) => {
          return {
            _id: t._id!,
            appliedAt: new Date(),
          };
        });

      let newContactTags = [...props.contact.tags, ...newTags];

      await ContactApi.update(
        {
          ...(props.contact as any),
          tags: newContactTags,
        },
        props.contact._id
      );

      setContacts((cs) =>
        cs
          ? cs.map((c) =>
              `${c._id}` === `${props.contact._id}`
                ? { ...props.contact, tags: newContactTags as any }
                : c
            )
          : []
      );
      setTagsSelected([]);
    } catch (err) {
      console.log(err);
    } finally {
      setIsApplyingTag(false);
    }
  }

  const handleRequestSort = (property: keyof ITagRow) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  function handleCheck(id: string) {
    setSelectedTags((elems) => {
      if (elems.includes(id)) {
        return elems.filter((check) => check !== id);
      }
      return [...elems, id];
    });
  }

  async function handleRemoveTag(tagId: string) {
    const response = await ContactApi.removeTag(tagId, contact._id);

    if (response) {
      setTagsInRow((tags) => tags.filter((t) => t._id !== tagId));

      if (searchContacts) {
        // Prop drilled way
        const { contacts, total } = await searchContacts();
        setContacts(contacts);
        setTotal(total);
      } else {
        // Old, refresh all screen way
        setContacts(
          (contacts) =>
            contacts &&
            contacts.map((c) => {
              if (c._id === contact._id)
                return { ...c, tags: c.tags.filter((t) => t._id !== tagId) };
              return c;
            })
        );
        setUpdateContactsFlag(true);
      }

      toast("Tag removed from contact", { type: "success" });
    }
  }

  function confirmDeleteTag(tag: { _id: string; name: string }) {
    modal.pushModal(
      <Warning
        icon={"./icons/delete.svg"}
        title="Remove Tag"
        description={`Are you sure you want to remove the tag “${tag.name}” from this contact?`}
        buttonsName={["No", "Yes"]}
        onOkClicked={async () => {
          try {
            handleRemoveTag(tag._id);
          } catch (e) {
            console.log(e);
          } finally {
            modal.popModal();
          }
        }}
        onCancelClicked={() => modal.popModal()}
      />
    );
  }

  if (loading)
    return (
      <Container>
        <TagsContainer>
          <SubTitle>Tags</SubTitle>
          <SearchContainer>
            <TagSelect
              tags={tagsSelected}
              preserveSystemTags={true}
              handleChoseTags={(tags) => {
                setTagsSelected(tags);
              }}
              cancelTagLoading={true}
            />
            <FilterTags
              filter={filter}
              handleChangeFilter={(vle) => setFilter(vle)}
            />
            <StyledButton css={button} onClick={() => {}}>
              Apply Tag
            </StyledButton>
          </SearchContainer>
        </TagsContainer>

        {Array(5)
          .fill(0)
          .map((e) => (
            <SkeletonCard
              css={css`
                width: 100%;
                height: 60px;
                margin-bottom: 10px;
              `}
            />
          ))}
      </Container>
    );

  if (isEmpty) {
    return getEmptyContainer();
  }

  function getEmptyContainer() {
    let textToShow: string;

    if (tagsSelected.length) {
      textToShow = "No tags matching your criteria";
    } else {
      if (filter.type.length) {
        textToShow = "No tags matching your criteria";
      } else {
        textToShow = "No tag to show";
      }
    }

    return (
      <EmptyContainer>
        <IconBerserkerMail name="tag-empty" />
        <SubTitle
          css={css`
            margin-top: 15.8px;
            margin-bottom: 15px;
            font-size: 16px;
          `}
        >
          {textToShow}
        </SubTitle>
        {!tagsSelected.length && !filter.type.length && (
          <StyledButton
            onClick={() => {
              modal.pushModal(
                <ApplyTagModal contact={props.contact} />,
                "Apply tag"
              );
            }}
          >
            Apply Tag
          </StyledButton>
        )}
      </EmptyContainer>
    );
  }

  return (
    <Container>
      {(filteredTags.length > 0 || filter.type.length > 0) && (
        <TagsContainer>
          <SubTitle>Tags</SubTitle>

          <SearchContainer>
            <TagSelect
              tags={tagsSelected}
              contactTags={contactITags}
              preserveSystemTags={true}
              handleChoseTags={(tags) => {
                setTagsSelected(tags);
              }}
            />
            <FilterTags
              filter={filter}
              handleChangeFilter={(vle) => setFilter(vle)}
            />
            <StyledButton
              css={button}
              disable={tagsSelected.length <= 0}
              isLoading={isApplyingTag}
              onClick={handleApplyTag}
            >
              Apply Tag
            </StyledButton>
          </SearchContainer>
        </TagsContainer>
      )}
      <TableContainer>
        {filteredTags.length > 0 ? (
          <>
            <TableCard>
              <TableHead>
                <FlexRow
                  onClick={() => handleRequestSort("name")}
                  css={css`
                    margin-left: 21px;
                  `}
                >
                  TAG
                  <Icon src={"./icons/contacts/arrow.svg"} />
                </FlexRow>
              </TableHead>

              <TableHead>
                <FlexRow onClick={() => handleRequestSort("category")}>
                  CATEGORY
                  <Icon src={"./icons/contacts/arrow.svg"} />
                </FlexRow>
              </TableHead>

              <TableHead style={{ marginLeft: "auto" }}>
                <FlexRow onClick={() => handleRequestSort("appliedAt")}>
                  DATE APPLIED
                  <Icon src={"./icons/contacts/arrow.svg"} />
                </FlexRow>
              </TableHead>
            </TableCard>

            {stableSort(filteredTags, getComparator(order, orderBy))
              .slice(
                page * (cachedRowsPerPage || rowsPerPage),
                page * (cachedRowsPerPage || rowsPerPage) +
                  (cachedRowsPerPage || rowsPerPage)
              )
              .map((tag) => {
                if (tag._id)
                  return (
                    <TagsTableRow
                      handleRemove={() => confirmDeleteTag({ ...tag })}
                      key={`${tag._id}_${tag.name}`}
                      row={tag}
                      isChecked={selectedTags.includes(tag._id)}
                      handleCheck={() => handleCheck(tag._id as string)}
                    />
                  );
              })}

            <TableFooter
              isSmaller
              page={page}
              length={
                stableSort(tagsInRow, getComparator(order, orderBy)).length || 0
              }
              rowsPerPage={cachedRowsPerPage || rowsPerPage}
              setRowsPerPage={(vle: number) => {
                setRowsPerPage(vle);
                updateCachedRowsPerPage(vle);
              }}
              handlePage={handlePage}
            />
          </>
        ) : (
          getEmptyContainer()
        )}
      </TableContainer>
    </Container>
  );
}
